import Decimal from "decimal.js";
import { Custom, CostRow, rowQuantity } from "./calculator";
import { resolveHelpers } from "./helpers";
const fmt = (value: Decimal.Value) => {
  const [whole, fraction] = new Decimal(value).toDecimalPlaces(4).toFixed().split(".");
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (fraction ? `.${fraction}` : "");
};
const tidy = (text: string) => text.replace(/\d+\.\d+/g, (value) => fmt(value));

export function quantityExplanation(item: Custom, row: CostRow): string {
  try {
    const source = row.quantitySource ?? "manual";
    const unoverridden = {
      ...item,
      rows: item.rows.map((r) =>
        r === row || (r.rowId === row.rowId && !!row.rowId) ? { ...r, quantityOverride: null } : r
      ),
    };
    const resolved = resolveHelpers(unoverridden, (r) => rowQuantity(unoverridden, r).quantity);
    const index = item.rows.indexOf(row);
    const effective = resolved.rows[index];
    let text = "";
    if (["duration", "travel", "duration_travel"].includes(source)) {
      const p = item.helpers?.production;
      const t = item.helpers?.travel;
      const day = item.helpers?.hoursPerDay ?? "8";
      const parts: string[] = [];
      if (source.includes("duration") && p && resolved.hours) {
        const quantity = resolved.hours.mul(p.rate).div(p.period === "day" ? day : 1);
        const name =
          p.source === "material"
            ? `Material quantity (${
                item.rows.find((r) => r.rowId === p.materialRowId)?.name ?? "material"
              })`
            : p.source === "manual"
            ? "Entered production quantity"
            : "Job size";
        parts.push(
          `${name}: ${fmt(quantity)} ${p.unit} ÷ production rate: ${fmt(p.rate)} ${p.unit}/${
            p.period
          }${p.period === "day" ? ` × ${fmt(day)} hours/day` : ""} = ${fmt(
            resolved.hours
          )} working hours.`
        );
      }
      if (source.includes("travel") && t) {
        const visits = t.visits ?? resolved.hours!.div(day).ceil().toString();
        parts.push(
          `Travel: ${fmt(visits)} visits × ${fmt(t.roundTripHours)} round-trip hours = ${fmt(
            resolved.travel
          )} travel hours.`
        );
        if (t.visits == null)
          parts.push(`Visits: working hours ÷ ${fmt(day)} hours/day, rounded up.`);
      }
      if (source === "duration_travel")
        parts.push(
          `${fmt(resolved.hours!)} working hours + ${fmt(resolved.travel)} travel hours = ${fmt(
            effective.quantity
          )} billed hours.`
        );
      text = parts.join(" ");
    } else if (
      row.category === "trucking" ||
      row.calculation !== "manual" ||
      source === "trucking"
    ) {
      const result = rowQuantity(
        unoverridden,
        source === "trucking" ? effective : { ...row, quantityOverride: null }
      );
      text = tidy(result.formula);
      if (row.category === "material") text += ` = ${fmt(result.quantity)} ${row.unit}.`;
    }
    if (row.quantityOverride != null)
      text += ` Using manually entered quantity: ${fmt(row.quantityOverride)} ${
        row.unit
      }. Restore calculation to use the calculated quantity.`;
    return text;
  } catch {
    return "Fix calculation inputs to see the formula.";
  }
}
export function costDescriptionExplanation(row: CostRow): string {
  if (!row.sourceType) return row.name;
  const type = {
    crew: "Crew",
    equipment: "Equipment",
    additional_cost: "Additional Cost",
    transfer_truck: "Transfer Truck",
    plant_product: "Plant Product",
  }[row.sourceType];
  return `${row.name}. ${type} library: ${row.sourceName ?? row.name}${
    row.sourcePlantName ? ` · Plant: ${row.sourcePlantName}` : ""
  }${
    row.sourceType === "transfer_truck" && row.truckCapacity
      ? ` · Capacity: ${row.truckCapacity} US tons`
      : ""
  }`;
}
